// PowerPoint Template Service following SRP, SOC, and DRY principles
// Uses PowerPointTemplateApiClient for all template-related operations

import { PowerPointTemplateApiClient } from "@/lib/api/clients/powerpoint-template-api-client";
import { AppBaseService as BaseService } from "./base/app-base-service";
import { ServiceResult } from "./base/service-types";
import type {
  TemplateScope,
  PowerPointTemplateResponse,
  TemplateListFilters,
  PowerPointTemplateUpdate,
  AvailableTemplatesResponse,
  TemplateUploadParams,
  PowerPointTemplateDeleteResponse,
  TemplateLoadResponse,
  TemplateAccessInfo,
} from "@/lib/types/api/powerpoint-template-types";

/**
 * PowerPoint Template Service
 *
 * Handles all PowerPoint template management operations:
 * - Template CRUD operations with scope validation
 * - Template analysis and placeholder detection
 * - Template listing with filtering and priority
 * - Usage analytics and statistics
 * - Access control and permissions
 *
 * Follows SRP: Single responsibility for template operations
 * Follows SOC: Separates template business logic from HTTP concerns
 * Follows DRY: Uses enhanced base service patterns
 */
export class PowerPointTemplateService extends BaseService {
  readonly serviceName = "PowerPointTemplateService";

  constructor(
    private powerPointTemplateApiClient: PowerPointTemplateApiClient = new PowerPointTemplateApiClient()
  ) {
    super();
  }

  // ========================================
  // Template CRUD Operations
  // ========================================

  /**
   * Upload a new PowerPoint template
   * @param params - Template upload parameters
   * @returns Promise<ServiceResult<PowerPointTemplateResponse>>
   */
  async uploadTemplate(
    params: TemplateUploadParams
  ): Promise<ServiceResult<PowerPointTemplateResponse>> {
    // Validate file before upload
    const validation = PowerPointTemplateApiClient.validateTemplateFile(
      params.file
    );
    if (!validation.valid) {
      return this.createErrorResult({
        message: validation.error!,
        code: "VALIDATION_ERROR",
      });
    }

    return this.executeWithErrorHandling(
      () => this.powerPointTemplateApiClient.uploadTemplate(params),
      {
        operation: "uploadTemplate",
        entity: "powerPointTemplate",
        metadata: {
          fileName: params.file.name,
          fileSize: params.file.size,
          scope: params.scope,
        },
      }
    );
  }

  /**
   * Request a presigned upload URL for PowerPoint template upload
   * @param params - Template upload parameters
   * @returns Promise<ServiceResult<{upload_url: string, s3_path: string, template_metadata: Record<string, any>}>>
   */
  async requestUploadUrl(params: {
    display_name: string;
    file_size: number;
    content_type: string;
    template_name: string;
    description?: string;
    category?: string;
    scope: TemplateScope;
    agent_id?: string;
    shared_scope_id?: string;
    is_default?: boolean;
    is_public?: boolean;
  }): Promise<
    ServiceResult<{
      upload_url: string;
      s3_path: string;
      template_metadata: Record<string, any>;
    }>
  > {
    return this.executeWithErrorHandling(
      () => this.powerPointTemplateApiClient.requestUploadUrl(params),
      {
        operation: "requestUploadUrl",
        entity: "powerPointTemplate",
        metadata: {
          template_name: params.template_name,
          file_size: params.file_size,
          scope: params.scope,
        },
      }
    );
  }

  /**
   * Complete template upload after file has been uploaded to S3
   * @param s3_path - The S3 path where the file was uploaded
   * @param template_metadata - The metadata returned from requestUploadUrl
   * @returns Promise<ServiceResult<PowerPointTemplateResponse>>
   */
  async completeUpload(
    s3_path: string,
    template_metadata: Record<string, any>
  ): Promise<ServiceResult<PowerPointTemplateResponse>> {
    return this.executeWithErrorHandling(
      () =>
        this.powerPointTemplateApiClient.completeUpload(
          s3_path,
          template_metadata
        ),
      {
        operation: "completeUpload",
        entity: "powerPointTemplate",
        metadata: {
          s3_path,
          template_name: template_metadata.template_name,
        },
      }
    );
  }

  /**
   * Get templates with optional filtering
   * @param filters - Optional filtering parameters
   * @returns Promise<ServiceResult<PowerPointTemplateResponse[]>>
   */
  async getTemplates(
    filters?: TemplateListFilters
  ): Promise<ServiceResult<PowerPointTemplateResponse[]>> {
    return this.executeWithErrorHandling(
      () => this.powerPointTemplateApiClient.getTemplates(filters),
      {
        operation: "getTemplates",
        entity: "powerPointTemplate",
        metadata: { filters },
      }
    );
  }

  /**
   * Get all templates available to current agent with priority information
   * @param agentId - Agent ID for agent-specific templates
   * @param sharedScopeId - Shared scope ID for organization templates
   * @returns Promise<ServiceResult<AvailableTemplatesResponse>>
   */
  async getAvailableTemplates(
    agentId?: string,
    sharedScopeId?: string
  ): Promise<ServiceResult<AvailableTemplatesResponse>> {
    return this.executeWithErrorHandling(
      () =>
        this.powerPointTemplateApiClient.getAvailableTemplates(
          agentId,
          sharedScopeId
        ),
      {
        operation: "getAvailableTemplates",
        entity: "powerPointTemplate",
      }
    );
  }

  /**
   * Get the default template for a specific scope
   * @param scope - Template scope
   * @param agentId - Agent ID for agent scope
   * @param sharedScopeId - Shared scope ID for shared scope
   * @returns Promise<ServiceResult<PowerPointTemplateResponse>>
   */
  async getDefaultTemplate(
    scope: TemplateScope,
    agentId?: string,
    sharedScopeId?: string
  ): Promise<ServiceResult<PowerPointTemplateResponse>> {
    this.validateRequired({ scope }, ["scope"], {
      operation: "getDefaultTemplate",
      entity: "powerPointTemplate",
    });

    return this.executeWithErrorHandling(
      () =>
        this.powerPointTemplateApiClient.getDefaultTemplate(
          scope,
          agentId,
          sharedScopeId
        ),
      {
        operation: "getDefaultTemplate",
        entity: "powerPointTemplate",
        entityId: `${scope}${agentId ? `-${agentId}` : ""}${sharedScopeId ? `-${sharedScopeId}` : ""}`,
        metadata: { scope, agentId, sharedScopeId },
      }
    );
  }

  /**
   * Get a specific template by ID
   * @param templateId - Template ID
   * @returns Promise<ServiceResult<PowerPointTemplateResponse>>
   */
  async getTemplate(
    templateId: string
  ): Promise<ServiceResult<PowerPointTemplateResponse>> {
    this.validateRequired({ templateId }, ["templateId"], {
      operation: "getTemplate",
      entity: "powerPointTemplate",
    });

    return this.executeWithErrorHandling(
      () => this.powerPointTemplateApiClient.getTemplate(templateId),
      {
        operation: "getTemplate",
        entity: "powerPointTemplate",
        entityId: templateId,
      }
    );
  }

  /**
   * Update template metadata
   * @param templateId - Template ID to update
   * @param updates - Partial template updates
   * @returns Promise<ServiceResult<PowerPointTemplateResponse>>
   */
  async updateTemplate(
    templateId: string,
    updates: PowerPointTemplateUpdate
  ): Promise<ServiceResult<PowerPointTemplateResponse>> {
    this.validateRequired({ templateId }, ["templateId"], {
      operation: "updateTemplate",
      entity: "powerPointTemplate",
    });

    return this.executeWithErrorHandling(
      () =>
        this.powerPointTemplateApiClient.updateTemplate(templateId, updates),
      {
        operation: "updateTemplate",
        entity: "powerPointTemplate",
        entityId: templateId,
        metadata: { fieldsUpdated: Object.keys(updates) },
      }
    );
  }

  /**
   * Delete a template
   * @param templateId - Template ID to delete
   * @param hardDelete - Whether to permanently delete
   * @returns Promise<ServiceResult<PowerPointTemplateDeleteResponse>>
   */
  async deleteTemplate(
    templateId: string,
    hardDelete: boolean = false
  ): Promise<ServiceResult<PowerPointTemplateDeleteResponse>> {
    this.validateRequired({ templateId }, ["templateId"], {
      operation: "deleteTemplate",
      entity: "powerPointTemplate",
    });

    return this.executeWithErrorHandling(
      () =>
        this.powerPointTemplateApiClient.deleteTemplate(templateId, hardDelete),
      {
        operation: "deleteTemplate",
        entity: "powerPointTemplate",
        entityId: templateId,
        metadata: { hardDelete },
      }
    );
  }

  // ========================================
  // Template Loading
  // ========================================

  /**
   * Load template for PowerPoint processing (internal use)
   * @param templateName - Template filename
   * @param agentId - Optional agent ID
   * @param sharedScopeId - Optional shared scope ID
   * @returns Promise<ServiceResult<TemplateLoadResponse>>
   */
  async loadTemplateForProcessing(
    templateName: string,
    agentId?: string,
    sharedScopeId?: string
  ): Promise<ServiceResult<TemplateLoadResponse>> {
    this.validateRequired({ templateName }, ["templateName"], {
      operation: "loadTemplateForProcessing",
      entity: "powerPointTemplate",
    });

    return this.executeWithErrorHandling(
      () =>
        this.powerPointTemplateApiClient.loadTemplateForProcessing(
          templateName,
          agentId,
          sharedScopeId
        ),
      {
        operation: "loadTemplateForProcessing",
        entity: "powerPointTemplate",
        metadata: { templateName, agentId, sharedScopeId },
      }
    );
  }

  // ========================================
  // Access Control
  // ========================================

  /**
   * Get access information for template operations
   * @param agentId - Current agent ID
   * @param sharedScopeId - Current shared scope ID
   * @param userRole - User role (for permission checking)
   * @returns TemplateAccessInfo
   */
  getAccessInfo(
    agentId?: string | null,
    sharedScopeId?: string | null,
    userRole: string = "user"
  ): TemplateAccessInfo {
    return {
      agent_id: agentId,
      shared_scope_id: sharedScopeId,
      available_scopes: this.getAvailableScopes(userRole),
      can_create_agent_templates: this.canCreateScope(userRole, "agent"),
      can_create_shared_templates: this.canCreateScope(userRole, "shared"),
    };
  }

  /**
   * Get scopes available to user role
   * @param userRole - User role
   * @returns TemplateScope[] - Available scopes
   */
  private getAvailableScopes(userRole: string): TemplateScope[] {
    const scopes: TemplateScope[] = [];

    // All users can create agent templates
    if (
      ["user", "agent_admin", "org_admin", "system_admin"].includes(userRole)
    ) {
      scopes.push("agent");
    }

    // Organization admins and system admins can create shared templates
    if (["org_admin", "system_admin"].includes(userRole)) {
      scopes.push("shared");
    }

    // Only system admins can create global templates
    if (userRole === "system_admin") {
      scopes.push("global");
    }

    return scopes;
  }

  /**
   * Check if user can create templates in given scope
   * @param userRole - User role
   * @param scope - Template scope
   * @returns boolean
   */
  private canCreateScope(userRole: string, scope: TemplateScope): boolean {
    switch (scope) {
      case "agent":
        return ["user", "agent_admin", "org_admin", "system_admin"].includes(
          userRole
        );
      case "shared":
        return ["org_admin", "system_admin"].includes(userRole);
      case "global":
        return userRole === "system_admin";
      default:
        return false;
    }
  }

  // ========================================
  // Utility Methods
  // ========================================

  /**
   * Format template placeholders for display
   * @param template - Template with placeholders
   * @returns string - Formatted placeholder summary
   */
  static formatPlaceholders(template: PowerPointTemplateResponse): string {
    return PowerPointTemplateApiClient.getPlaceholderSummary(
      template.placeholders
    );
  }

  /**
   * Get template scope display information
   * @param scope - Template scope
   * @returns object with display name and description
   */
  static getScopeInfo(scope: TemplateScope) {
    return {
      displayName: PowerPointTemplateApiClient.getScopeDisplayName(scope),
      description: PowerPointTemplateApiClient.getScopeDescription(scope),
    };
  }

  /**
   * Validate template file
   * @param file - File to validate
   * @returns Validation result
   */
  static validateFile(file: File) {
    return PowerPointTemplateApiClient.validateTemplateFile(file);
  }

  /**
   * Format file size for display
   * @param bytes - File size in bytes
   * @returns Formatted size string
   */
  static formatFileSize(bytes: number): string {
    return PowerPointTemplateApiClient.formatFileSize(bytes);
  }

  /**
   * Check if template can be edited by current user
   * @param template - Template to check
   * @param currentAgentId - Current agent ID
   * @param userRole - Current user role
   * @returns boolean
   */
  static canEditTemplate(
    template: PowerPointTemplateResponse,
    currentAgentId?: string,
    userRole?: string
  ): boolean {
    // Global templates can only be edited by system admins
    if (template.scope === "global") {
      return userRole === "system_admin";
    }

    // Shared templates can be edited by org admins and system admins
    if (template.scope === "shared") {
      return ["org_admin", "system_admin"].includes(userRole || "");
    }

    // Agent templates can be edited by the owning agent or admins
    if (template.scope === "agent") {
      return (
        template.agent_id === currentAgentId ||
        ["org_admin", "system_admin"].includes(userRole || "")
      );
    }

    return false;
  }

  /**
   * Check if user can upload templates in given scope
   * @param scope - Template scope to check
   * @param userRole - Current user role
   * @returns boolean
   */
  static canUploadTemplate(scope: TemplateScope, userRole?: string): boolean {
    switch (scope) {
      case "agent":
        return ["user", "agent_admin", "org_admin", "system_admin"].includes(
          userRole || ""
        );
      case "shared":
        return ["org_admin", "system_admin"].includes(userRole || "");
      case "global":
        return userRole === "system_admin";
      default:
        return false;
    }
  }

  /**
   * Check if template can be deleted by current user
   * @param template - Template to check
   * @param currentAgentId - Current agent ID
   * @param userRole - Current user role
   * @returns boolean
   */
  static canDeleteTemplate(
    template: PowerPointTemplateResponse,
    currentAgentId?: string,
    userRole?: string
  ): boolean {
    // Same rules as editing, but more restrictive
    return this.canEditTemplate(template, currentAgentId, userRole);
  }
}

/**
 * Default singleton instance for ease of use
 * Can be overridden with dependency injection in tests
 */
export const powerPointTemplateService = new PowerPointTemplateService();
